import * as angular from 'angular';

import { StorageService } from '../services/storage-service';

interface IAccordionPanelMap {
    [id: string]: JQuery;
}

interface IAccordionScope extends angular.IScope {
    key: string;
    change: () => void;
}

class AccordionController implements IAccordionController {
    static readonly $inject = ['$scope', '$element', 'fcStorageService'];

    private nextId = 1;
    private activeId = `${1}`;
    private readonly panels: IAccordionPanelMap = {};

    constructor(
        private readonly scope: IAccordionScope,
        private readonly element: JQuery,
        private readonly storageService: StorageService
    ) {
        // Get the stored panel id if there is one
        if (this.scope.key) {
            const activeId = this.storageService.get(`${this.scope.key}__accordion`);

            if (activeId) {
                this.activeId = activeId;
            }
        }

        // Hook the accordion down event to control the element classes
        this.element.on('down.zf.accordion', (_event, trigger) => {
            const id = trigger.closest('.fc-accordion-panel').index() + 1;
            this.activatePanel(`${id}`);

            // Emit an event when the active panel changes
            if (this.scope.change) {
                this.scope.change();
            }
        });

        this.scope.$on('$destroy', () => {
            this.element.off();
        });
    }

    registerPanel(element: JQuery): void {
        const id = `${this.nextId++}`;
        this.panels[id] = element;

        // Set the active id if the panel is registering as active
        if (id === this.activeId) {
            this.activatePanel(this.activeId);
        }
    }

    ensurePanelActive(): void {
        const ids = Object.keys(this.panels);
        const panel = this.panels[this.activeId];

        // If no panel is active, just pick the first one
        if (!panel && ids.length > 0) {
            this.activeId = ids[0];
            this.activatePanel(this.activeId);
        }
    }

    private activatePanel(activeId: string): void {
        // Cycle the panels and adjust the active classes
        Object.keys(this.panels).forEach(id => {
            const panel = this.panels[id];

            if (id === activeId) {
                panel.addClass(['fc-accordion-active', 'is-active']);
            } else {
                panel.removeClass(['fc-accordion-active', 'is-active']);
            }
        });

        // Then record the active id is a key is set
        if (this.scope.key) {
            this.storageService.set(`${this.scope.key}__accordion`, activeId);
        }
    }
}

class AccordionDirective implements angular.IDirective {
    readonly replace = true;
    readonly transclude = true;
    readonly restrict = 'E';
    readonly controller = AccordionController;
    readonly scope = {
        key: '@',
        change: '&?change'
    };

    readonly template = `
        <ul
            class="fc-accordion accordion"
            layout="column"
            data-accordion
            data-slide-speed="0"
            ng-transclude
        ></ul>
    `;

    readonly link: angular.IDirectiveLinkFn;

    constructor() {
        this.link = this._link.bind(this);
    }

    private _link(
        _scope: angular.IScope,
        _element: JQuery,
        _attributes: angular.IAttributes,
        controller: AccordionController
    ): void {
        // Always ensure a panel is active
        controller.ensurePanelActive();
    }
}

export interface IAccordionController {
    registerPanel(element: JQuery): void;
}

const AccordionDirectiveFactory: angular.IDirectiveFactory = () => new AccordionDirective();

export { AccordionDirectiveFactory };
